import React, { useState, useEffect, useCallback } from 'react';
import { ABTest } from '../../../types/template';
import { WinnerDetectionEngine, WinnerDetectionResult, WinnerDetectionAlert, RealTimeMonitoringData } from '../../../services/winnerDetectionEngine';
import { AutomatedImplementationService, ImplementationResult } from '../../../services/automatedImplementationService';

interface RealTimeMonitorProps {
  test: ABTest;
  onWinnerDetected: (result: WinnerDetectionResult) => void;
  onImplementationComplete: (result: ImplementationResult) => void;
  onAlert: (alert: WinnerDetectionAlert) => void;
}

interface MonitoringStats {
  currentLeader: string;
  confidence: number;
  improvement: number;
  sampleSize: number;
  timeRemaining: string;
  significance: boolean;
}

const RealTimeMonitor: React.FC<RealTimeMonitorProps> = ({
  test,
  onWinnerDetected,
  onImplementationComplete,
  onAlert
}) => {
  const [_monitoringData, setMonitoringData] = useState<RealTimeMonitoringData | null>(null);
  const [winnerResult, setWinnerResult] = useState<WinnerDetectionResult | null>(null);
  const [implementationResult, setImplementationResult] = useState<ImplementationResult | null>(null);
  const [alerts, setAlerts] = useState<WinnerDetectionAlert[]>([]);
  const [isMonitoring, setIsMonitoring] = useState(false);
  const [stats, setStats] = useState<MonitoringStats | null>(null);

  // Initialize services
  const [winnerEngine] = useState(() => new WinnerDetectionEngine({
    confidenceThreshold: 95,
    minimumSampleSize: 1000,
    minimumTestDuration: 24,
    minimumImprovementThreshold: 5,
    earlyStoppingEnabled: true,
    riskTolerance: 'moderate'
  }));

  const [implementationService] = useState(() => new AutomatedImplementationService({
    autoImplementEnabled: true,
    requiresApproval: true,
    approvalThreshold: 95,
    backupRetentionDays: 30,
    rollbackEnabled: true,
    notificationEnabled: true,
    safetyChecks: {
      performanceValidation: true,
      brandGuidelineCheck: true,
      accessibilityValidation: true,
      contentModerationCheck: true
    }
  }));

  // Set up alert callback
  useEffect(() => {
    winnerEngine.onAlert((alert) => {
      setAlerts(prev => [alert, ...prev].slice(0, 10)); // Keep last 10 alerts
      onAlert(alert);
    });
  }, [winnerEngine, onAlert]);

  // Start monitoring when test is running
  useEffect(() => {
    if (test.status === 'running' && !isMonitoring) {
      startMonitoring();
    } else if (test.status !== 'running' && isMonitoring) {
      stopMonitoring();
    }
  }, [test.status, isMonitoring]);

  const startMonitoring = useCallback(() => {
    setIsMonitoring(true);
    
    // Simulate real-time monitoring with periodic checks
    const monitoringInterval = setInterval(() => {
      try {
        // Analyze current test state
        const result = winnerEngine.analyzeTest(test);
        setWinnerResult(result);

        // Get monitoring data
        const data = winnerEngine.monitorTestRealTime(test.id);
        setMonitoringData(data);

        // Update stats
        updateMonitoringStats(result, data);

        // Check for winner
        if (result.hasWinner && result.recommendation === 'implement') {
          onWinnerDetected(result);
          
          // Auto-implement if configured
          handleAutoImplementation(result);
        }

      } catch (error) {
        console.error('Monitoring error:', error);
      }
    }, 5000); // Check every 5 seconds

    // Store interval ID for cleanup
    (window as any).monitoringInterval = monitoringInterval;
  }, [test, winnerEngine, onWinnerDetected]);

  const stopMonitoring = useCallback(() => {
    setIsMonitoring(false);
    if ((window as any).monitoringInterval) {
      clearInterval((window as any).monitoringInterval);
      delete (window as any).monitoringInterval;
    }
  }, []);

  const handleAutoImplementation = useCallback(async (result: WinnerDetectionResult) => {
    if (!result.hasWinner) return;

    try {
      // Create a mock original template for implementation
      const originalTemplate = {
        ...test.variants.find(v => v.isControl)?.template,
        id: test.templateId,
        name: test.templateName || 'Test Template'
      };

      if (!originalTemplate) {
        throw new Error('Original template not found');
      }

      const implResult = await implementationService.implementWinner(
        test,
        result,
        originalTemplate as any
      );

      setImplementationResult(implResult);
      onImplementationComplete(implResult);

    } catch (error) {
      console.error('Auto-implementation failed:', error);
    }
  }, [test, implementationService, onImplementationComplete]);

  const updateMonitoringStats = useCallback((
    result: WinnerDetectionResult,
    data: RealTimeMonitoringData | null
  ) => {
    if (!data) return;

    const controlVariant = data.variants.find(v => v.variantId === 'control');
    const testVariant = data.variants.find(v => v.variantId !== 'control');

    if (!controlVariant || !testVariant) return;

    const currentLeader = testVariant.conversionRate > controlVariant.conversionRate ? 'Variant' : 'Control';
    const totalSampleSize = controlVariant.sampleSize + testVariant.sampleSize;

    // Calculate estimated time remaining
    const testDuration = test.startedAt ? Date.now() - test.startedAt.getTime() : 0;
    const estimatedDuration = test.estimatedCompletionAt ? test.estimatedCompletionAt.getTime() - test.startedAt!.getTime() : 14 * 24 * 60 * 60 * 1000;
    const timeRemaining = Math.max(0, estimatedDuration - testDuration);
    const timeRemainingStr = formatTimeRemaining(timeRemaining);

    setStats({
      currentLeader,
      confidence: result.confidence,
      improvement: result.improvement,
      sampleSize: totalSampleSize,
      timeRemaining: timeRemainingStr,
      significance: result.statisticalAnalysis.statistical.isSignificant
    });
  }, [test]);

  const formatTimeRemaining = (milliseconds: number): string => {
    const days = Math.floor(milliseconds / (24 * 60 * 60 * 1000));
    const hours = Math.floor((milliseconds % (24 * 60 * 60 * 1000)) / (60 * 60 * 1000));
    const minutes = Math.floor((milliseconds % (60 * 60 * 1000)) / (60 * 1000));

    if (days > 0) return `${days}d ${hours}h`;
    if (hours > 0) return `${hours}h ${minutes}m`;
    return `${minutes}m`;
  };

  const _getStatusColor = (status: string) => {
    switch (status) {
      case 'running': return 'text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30';
      case 'winner_detected': return 'text-green-600 dark:text-green-300 bg-green-50 dark:bg-green-900/30';
      case 'implemented': return 'text-purple-600 dark:text-purple-300 bg-purple-50 dark:bg-purple-900/30';
      default: return 'text-content-secondary bg-surface-hover';
    }
  };

  const getAlertColor = (severity: WinnerDetectionAlert['severity']) => {
    switch (severity) {
      case 'critical': return 'text-red-600 dark:text-red-300 bg-red-50 dark:bg-red-900/30 border-red-200 dark:border-red-800/50';
      case 'warning': return 'text-yellow-600 dark:text-yellow-300 bg-yellow-50 dark:bg-yellow-900/30 border-yellow-200 dark:border-yellow-800/50';
      case 'info': return 'text-blue-600 dark:text-blue-300 bg-blue-50 dark:bg-blue-900/30 border-blue-200 dark:border-blue-800/50';
      default: return 'text-content-secondary bg-surface-hover border-line';
    }
  };

  if (test.status !== 'running') {
    return (
      <div className="bg-surface rounded-lg shadow-sm border border-line p-6">
        <div className="text-center py-8">
          <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 dark:bg-dark-700 rounded-full flex items-center justify-center">
            <svg className="w-8 h-8 text-content-subtle" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 12H4" />
            </svg>
          </div>
          <h3 className="text-lg font-medium text-content mb-2">Test Not Running</h3>
          <p className="text-content-secondary">Real-time monitoring is only available for running tests.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Monitoring Status */}
      <div className="bg-surface rounded-lg shadow-sm border border-line p-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-content">Real-Time Monitoring</h3>
          <div className="flex items-center space-x-2">
            <div className={`w-3 h-3 rounded-full ${isMonitoring ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></div>
            <span className="text-sm text-content-secondary">
              {isMonitoring ? 'Monitoring Active' : 'Monitoring Inactive'}
            </span>
          </div>
        </div>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-content">{stats.currentLeader}</div>
              <div className="text-sm text-content-secondary">Current Leader</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600 dark:text-blue-300">{stats.confidence}%</div>
              <div className="text-sm text-content-secondary">Confidence</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${stats.improvement >= 0 ? 'text-green-600 dark:text-green-300' : 'text-red-600 dark:text-red-300'}`}>
                {stats.improvement >= 0 ? '+' : ''}{stats.improvement.toFixed(1)}%
              </div>
              <div className="text-sm text-content-secondary">Improvement</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-content">{stats.sampleSize.toLocaleString()}</div>
              <div className="text-sm text-content-secondary">Sample Size</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-purple-600 dark:text-purple-300">{stats.timeRemaining}</div>
              <div className="text-sm text-content-secondary">Time Remaining</div>
            </div>
            <div className="text-center">
              <div className={`text-2xl font-bold ${stats.significance ? 'text-green-600 dark:text-green-300' : 'text-content-subtle'}`}>
                {stats.significance ? '✓' : '○'}
              </div>
              <div className="text-sm text-content-secondary">Significant</div>
            </div>
          </div>
        )}
      </div>

      {/* Winner Detection Status */}
      {winnerResult && (
        <div className="bg-surface rounded-lg shadow-sm border border-line p-6">
          <h3 className="text-lg font-semibold text-content mb-4">Winner Detection Status</h3>
          
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Recommendation:</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${
                winnerResult.recommendation === 'implement' ? 'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300' :
                winnerResult.recommendation === 'continue' ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 dark:text-blue-300' :
                'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300'
              }`}>
                {winnerResult.recommendation.replace('_', ' ').toUpperCase()}
              </span>
            </div>

            <div className="bg-gray-50 dark:bg-dark-700 rounded-lg p-4">
              <p className="text-sm text-gray-700 dark:text-gray-300">{winnerResult.reasoning}</p>
            </div>

            {winnerResult.nextActions.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Next Actions:</h4>
                <ul className="space-y-1">
                  {winnerResult.nextActions.map((action, index) => (
                    <li key={index} className="flex items-start space-x-2 text-sm text-content-secondary">
                      <span className="text-blue-500 dark:text-blue-400 mt-1">•</span>
                      <span>{action}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Implementation Status */}
      {implementationResult && (
        <div className="bg-surface rounded-lg shadow-sm border border-line p-6">
          <h3 className="text-lg font-semibold text-content mb-4">Implementation Status</h3>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-3">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                implementationResult.success ? 'bg-green-100 dark:bg-green-900/30' : 'bg-red-100 dark:bg-red-900/30'
              }`}>
                {implementationResult.success ? (
                  <svg className="w-5 h-5 text-green-600 dark:text-green-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                ) : (
                  <svg className="w-5 h-5 text-red-600 dark:text-red-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                )}
              </div>
              <div>
                <div className="font-medium text-content">
                  {implementationResult.success ? 'Implementation Successful' : 'Implementation Failed'}
                </div>
                <div className="text-sm text-content-secondary">
                  {implementationResult.timestamp.toLocaleString()}
                </div>
              </div>
            </div>

            {implementationResult.changesApplied.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Changes Applied:</h4>
                <div className="space-y-2">
                  {implementationResult.changesApplied.map((change, index) => (
                    <div key={index} className="bg-gray-50 dark:bg-dark-700 rounded-lg p-3">
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-content">{change.description}</span>
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          change.impact === 'high' ? 'bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300' :
                          change.impact === 'medium' ? 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 dark:text-yellow-300' :
                          'bg-green-100 dark:bg-green-900/30 text-green-800 dark:text-green-300'
                        }`}>
                          {change.impact} impact
                        </span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {implementationResult.errors && implementationResult.errors.length > 0 && (
              <div>
                <h4 className="text-sm font-medium text-red-700 dark:text-red-300 mb-2">Errors:</h4>
                <ul className="space-y-1">
                  {implementationResult.errors.map((error, index) => (
                    <li key={index} className="text-sm text-red-600 dark:text-red-300">• {error}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Alerts */}
      {alerts.length > 0 && (
        <div className="bg-surface rounded-lg shadow-sm border border-line p-6">
          <h3 className="text-lg font-semibold text-content mb-4">Recent Alerts</h3>
          
          <div className="space-y-3">
            {alerts.slice(0, 5).map((alert) => (
              <div key={alert.id} className={`border rounded-lg p-3 ${getAlertColor(alert.severity)}`}>
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <span className="text-sm font-medium capitalize">{alert.type.replace('_', ' ')}</span>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        alert.severity === 'critical' ? 'bg-red-200 dark:bg-red-900/40 text-red-800 dark:text-red-300' :
                        alert.severity === 'warning' ? 'bg-yellow-200 dark:bg-yellow-900/40 text-yellow-800 dark:text-yellow-300' :
                        'bg-blue-200 dark:bg-blue-900/40 text-blue-800 dark:text-blue-300'
                      }`}>
                        {alert.severity}
                      </span>
                    </div>
                    <p className="text-sm mt-1">{alert.message}</p>
                  </div>
                  <div className="text-xs text-content-muted ml-4">
                    {alert.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Performance Chart Placeholder */}
      <div className="bg-surface rounded-lg shadow-sm border border-line p-6">
        <h3 className="text-lg font-semibold text-content mb-4">Performance Trend</h3>
        <div className="h-64 bg-gray-50 dark:bg-dark-700 rounded-lg flex items-center justify-center">
          <div className="text-center">
            <svg className="w-12 h-12 text-content-subtle mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
            </svg>
            <p className="text-content-secondary">Real-time performance chart would be displayed here</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RealTimeMonitor;